import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Telescope, Rocket, Gem, Atom, Orbit, BrainCircuit, Waypoints, Fingerprint, CircuitBoard } from 'lucide-react';

export default function AboutSection() {
  const { t, i18n } = useTranslation();
  const isEN = i18n.language === 'en';

  const values = [
    { key: 'vision', icon: Telescope, accent: '#0ea5e9' },
    { key: 'mission', icon: Rocket, accent: '#10b981' },
    { key: 'values', icon: Gem, accent: '#8b5cf6' },
  ];

  const techs = [
    { icon: Atom, accent: '#06b6d4', fr: 'Informatique Quantique', en: 'Quantum Computing' },
    { icon: Orbit, accent: '#8b5cf6', fr: 'Nanotechnologie', en: 'Nanotechnology' },
    { icon: BrainCircuit, accent: '#10b981', fr: 'Intelligence Artificielle', en: 'Artificial Intelligence' },
    { icon: Waypoints, accent: '#0ea5e9', fr: 'Réseaux Neuronaux', en: 'Neural Networks' },
    { icon: Fingerprint, accent: '#d946ef', fr: 'Cybersécurité', en: 'Cybersecurity' },
    { icon: CircuitBoard, accent: '#14b8a6', fr: 'Edge Computing', en: 'Edge Computing' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  const techVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  const stats = [
    { value: '6', suffix: ' ans', label: 'd\'expérience' },
    { value: '40', suffix: '+', label: 'projets livrés' },
    { value: '98', suffix: '%', label: 'clients satisfaits' },
    { value: '12', suffix: '', label: 'experts tech' },
  ];

  return (
    <section id="about" className="relative py-24 md:py-32" data-testid="about-section">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-16 max-w-2xl"
        >
          <span className="inline-block font-outfit text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">
            {t('about.label')}
          </span>
          <h2 className="font-outfit font-medium text-3xl md:text-4xl lg:text-5xl tracking-tight text-zinc-950 mb-5">
            {t('about.title')}{' '}
            <span className="text-gradient">{t('about.titleHighlight')}</span>
          </h2>
          <p className="font-manrope text-base md:text-lg text-zinc-600 leading-relaxed">
            {t('about.description')}
          </p>
        </motion.div>

        {/* Values Grid — elevated icons */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16"
        >
          {values.map((item) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.key}
                variants={itemVariants}
                className="group p-8 soft-card relative overflow-hidden"
                data-testid={`about-${item.key}`}
              >
                {/* accent glow */}
                <div
                  className="absolute -top-10 -right-10 w-32 h-32 opacity-40 blur-2xl transition-opacity duration-300 group-hover:opacity-70"
                  style={{ background: `radial-gradient(circle, ${item.accent}, transparent 70%)` }}
                />
                <div
                  className="relative w-14 h-14 flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110"
                  style={{
                    background: `linear-gradient(135deg, ${item.accent}22, #ffffff)`,
                    border: `1px solid ${item.accent}44`,
                    boxShadow: `0 10px 26px -10px ${item.accent}66`,
                  }}
                >
                  <Icon className="w-6 h-6" style={{ color: item.accent }} strokeWidth={1.6} />
                </div>
                <h3 className="relative font-outfit font-medium text-xl text-zinc-900 mb-3">
                  {t(`about.${item.key}.title`)}
                </h3>
                <p className="relative font-manrope text-sm text-zinc-600 leading-relaxed">
                  {t(`about.${item.key}.text`)}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Future tech symbols strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-16"
          data-testid="future-tech"
        >
          <div className="flex items-center gap-3 mb-8">
            <span className="font-outfit text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold whitespace-nowrap">
              {isEN ? 'Future technologies we explore' : 'Les technologies d\'avenir que nous explorons'}
            </span>
            <span className="h-px flex-1 bg-gradient-to-r from-zinc-300 to-transparent" />
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
          >
            {techs.map((tech, i) => {
              const Icon = tech.icon;
              return (
                <motion.div
                  key={i}
                  variants={techVariants}
                  className="group relative p-5 soft-card flex flex-col items-center text-center overflow-hidden"
                  data-testid={`tech-symbol-${i}`}
                >
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ background: `radial-gradient(circle at 50% 0%, ${tech.accent}12, transparent 70%)` }}
                  />
                  <motion.div
                    className="relative w-12 h-12 flex items-center justify-center mb-3"
                    style={{
                      background: `linear-gradient(135deg, ${tech.accent}1f, #ffffff)`,
                      border: `1px solid ${tech.accent}3a`,
                    }}
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 3 + i * 0.4, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <Icon className="w-6 h-6" style={{ color: tech.accent }} strokeWidth={1.5} />
                  </motion.div>
                  <span className="relative font-outfit text-xs font-medium text-zinc-700 leading-tight">
                    {isEN ? tech.en : tech.fr}
                  </span>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 py-10 border-t border-zinc-200"
        >
          {stats.map((stat, index) => (
            <div key={index} className="text-center md:text-left">
              <div className="font-outfit font-semibold text-3xl md:text-4xl tracking-tight text-zinc-950 mb-1">
                {stat.value}<span className="text-xl text-sky-500">{stat.suffix}</span>
              </div>
              <div className="font-manrope text-xs md:text-sm text-zinc-500">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
